/**
 * The dials a player may turn on an automatic chart.
 *
 * This file is the browser's half of a contract whose other half is the
 * engine's `generation/tuning.py`, and it deliberately holds no generation
 * logic at all: the numbers here are *positions*, and what a position means —
 * which beat step it selects, which threshold it lowers — is decided in Python,
 * once. Reimplementing any of that on this side would give Mandalizm two chart
 * engines that agree until they do not.
 *
 * What does live here is the shape of a correction and the arithmetic of the
 * three plain-language answers, because those are interface, not generation:
 * "too busy" has to mean something before a request can be sent.
 */

/** AUTO is the engine deciding everything, as it always has. */
/**
 * How much of the chart the engine decided.
 *
 * `DIRECTED_AUTO` is the engine using its own direction system: it detects
 * where the song changes and writes shapes, traversals, density and source
 * balance per section. AUTO stays exactly what it always was.
 */
export type GeneratorMode = 'AUTO' | 'CUSTOM' | 'DIRECTED_AUTO';

export interface ChartTuning {
  /** Note count against this difficulty's own baseline. 1 leaves it alone. */
  readonly density: number;
  /** Multiplies the difficulty's minimum spacing. 1 leaves it alone. */
  readonly minimumIntervalScale: number;
  /** How readily a peak in the audio counts as an onset. 0.5 is the default. */
  readonly onsetSensitivity: number;
  /** Moves every event, and the beat grid with it. */
  readonly syncOffsetMs: number;
  /** How far consecutive seals are pushed apart. Spatial only. */
  readonly movement: number;
}

/** Every dial where the engine's own configuration already sits. */
export const NEUTRAL_TUNING: ChartTuning = Object.freeze({
  density: 1,
  minimumIntervalScale: 1,
  onsetSensitivity: 0.5,
  syncOffsetMs: 0,
  movement: 0.5,
});

export interface TuningRange {
  readonly key: keyof ChartTuning;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  /** Rendered value, e.g. `400 ms` rather than `1.00`. */
  readonly format: (value: number, auto: AutoParameters | null) => string;
  readonly hint: string;
}

/**
 * What the engine measured, and what it merely assumed.
 *
 * The split is the whole reason this type is not a flat bag of numbers. BPM is
 * a property of the song; density 1.00 is a property of the config file and is
 * the same 1.00 for every song ever generated. Showing them in one list under
 * the heading "AUTO ANALYSIS" would tell the player the engine listened for
 * both, so the two travel separately all the way to the screen.
 */
export interface AutoParameters {
  readonly seed: number;
  readonly measured: {
    readonly detectedBpm: number;
    readonly firstBeatOffsetSeconds: number;
    readonly durationSeconds: number;
    readonly beatCount: number;
    readonly beatIntervalSeconds: number;
    readonly onsetCount: number;
  };
  readonly defaults: {
    readonly density: number;
    readonly minimumIntervalScale: number;
    readonly onsetSensitivity: number;
    readonly syncOffsetMs: number;
    readonly movement: number;
    readonly onsetSensitivityRaw: number;
    readonly minimumEventIntervalSeconds: Readonly<Record<string, number>>;
    readonly beatStep: Readonly<Record<string, number>>;
  };
}

/** Per difficulty, how many events came from where. */
export type EventSources = Readonly<Record<string, Readonly<Record<string, number>>>>;

const RANGES: Readonly<Record<keyof ChartTuning, { min: number; max: number }>> = Object.freeze({
  density: { min: 0.5, max: 1.5 },
  minimumIntervalScale: { min: 0.5, max: 2 },
  onsetSensitivity: { min: 0, max: 1 },
  syncOffsetMs: { min: -150, max: 150 },
  movement: { min: 0, max: 1 },
});

export function clampTuning(tuning: ChartTuning): ChartTuning {
  const clamp = (key: keyof ChartTuning): number => {
    const { min, max } = RANGES[key];
    const value = tuning[key];
    return !Number.isFinite(value) ? NEUTRAL_TUNING[key] : Math.min(max, Math.max(min, value));
  };
  return {
    density: clamp('density'),
    minimumIntervalScale: clamp('minimumIntervalScale'),
    onsetSensitivity: clamp('onsetSensitivity'),
    syncOffsetMs: clamp('syncOffsetMs'),
    movement: clamp('movement'),
  };
}

export function isNeutral(tuning: ChartTuning): boolean {
  return (Object.keys(NEUTRAL_TUNING) as (keyof ChartTuning)[]).every(
    (key) => Math.abs(tuning[key] - NEUTRAL_TUNING[key]) < 1e-9,
  );
}

/**
 * The three answers, as arithmetic.
 *
 * A player who will never open the advanced panel still has an opinion about
 * the chart they just played, and it is usually one of these. Each answer is a
 * *correction of the current position*, not a jump to a preset: saying "too
 * busy" twice thins the chart twice, which is what someone pressing it twice
 * means.
 */
export function applyFeedback(tuning: ChartTuning, feedback: 'busy' | 'good' | 'empty'): ChartTuning {
  if (feedback === 'good') return tuning;
  if (feedback === 'busy') {
    return clampTuning({
      ...tuning,
      density: tuning.density * 0.8,
      minimumIntervalScale: tuning.minimumIntervalScale * 1.25,
    });
  }
  return clampTuning({
    ...tuning,
    density: tuning.density * 1.2,
    onsetSensitivity: tuning.onsetSensitivity + 0.1,
  });
}

/** The rows the advanced panel shows, in the order it shows them. */
export const TUNING_ROWS: readonly TuningRange[] = Object.freeze([
  {
    key: 'density',
    label: 'DENSITY',
    min: RANGES.density.min,
    max: RANGES.density.max,
    step: 0.05,
    format: (value) => `${value.toFixed(2)}×`,
    hint: 'How many notes, against this difficulty’s own baseline.',
  },
  {
    key: 'minimumIntervalScale',
    label: 'MIN INTERVAL',
    min: RANGES.minimumIntervalScale.min,
    max: RANGES.minimumIntervalScale.max,
    step: 0.05,
    // Shown in milliseconds because that is what the dial does to the chart,
    // using NORMAL's baseline as the reference the player can feel.
    format: (value, auto) => {
      const baseline = auto?.defaults.minimumEventIntervalSeconds['normal'] ?? 0.4;
      return `${Math.round(baseline * value * 1000)} ms`;
    },
    hint: 'The shortest gap between two notes.',
  },
  {
    key: 'onsetSensitivity',
    label: 'ONSET SENSITIVITY',
    min: RANGES.onsetSensitivity.min,
    max: RANGES.onsetSensitivity.max,
    step: 0.05,
    format: (value) => value.toFixed(2),
    hint: 'How readily a hit in the audio becomes a note.',
  },
  {
    key: 'syncOffsetMs',
    label: 'SYNC OFFSET',
    min: RANGES.syncOffsetMs.min,
    max: RANGES.syncOffsetMs.max,
    step: 5,
    format: (value) => `${value > 0 ? '+' : ''}${Math.round(value)} ms`,
    hint: 'Moves the whole chart earlier or later.',
  },
  {
    key: 'movement',
    label: 'MOVEMENT',
    min: RANGES.movement.min,
    max: RANGES.movement.max,
    step: 0.05,
    format: (value) => value.toFixed(2),
    hint: 'How far apart consecutive seals are placed. Timing is untouched.',
  },
]);
